// src/components/SiteBuilder.tsx
// "Constructor de Sitio" — admin activa/desactiva/reordena secciones del sitio público.
// Drag & drop: HTML5 nativo (sin dnd-kit). Side panel para editar config por sección.
// Design system: Orion glass-morphism (o-card, --acc1/2/3).

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { getAdminSiteConfig, updateAdminSiteConfig, type SiteSection } from '@/lib/api';
import { invalidateSiteConfigCache } from '@/lib/siteConfigStore';
import {
  Loader, Save, GripVertical, ChevronRight, X, Globe,
  Image, List, BookOpen, Star, HelpCircle, Mail, MessageCircle, Users, LayoutTemplate,
} from 'lucide-react';

/* ═══════════════════════════════════════════════════════════════
   Section metadata
   ═══════════════════════════════════════════════════════════════ */

interface SectionMeta {
  label: string;
  icon: React.ReactNode;
  description: string;
  configFields: ConfigField[];
}

interface ConfigField {
  key: string;
  label: string;
  type: 'text' | 'textarea' | 'url' | 'number' | 'select';
  placeholder?: string;
  options?: string[];
}

const SECTION_META: Record<string, SectionMeta> = {
  hero: {
    label: 'Hero / Portada',
    icon: <Image size={16} />,
    description: 'Banner principal con imagen de fondo y CTA',
    configFields: [
      { key: 'title', label: 'Título', type: 'text', placeholder: 'Descubre el mundo' },
      { key: 'subtitle', label: 'Subtítulo', type: 'text', placeholder: 'Viajes únicos para cada momento' },
      { key: 'background_image_url', label: 'URL imagen de fondo', type: 'url', placeholder: 'https://…' },
      { key: 'cta_text', label: 'Texto del botón CTA', type: 'text', placeholder: 'Ver viajes' },
      { key: 'cta_url', label: 'URL del botón CTA', type: 'url', placeholder: '/viajes' },
    ],
  },
  trips: {
    label: 'Catálogo de viajes',
    icon: <List size={16} />,
    description: 'Muestra los viajes destacados',
    configFields: [
      { key: 'title', label: 'Título', type: 'text', placeholder: 'Nuestros viajes' },
      { key: 'subtitle', label: 'Subtítulo', type: 'text', placeholder: 'Explora nuestras rutas' },
      { key: 'max_items_featured', label: 'Nº máximo de items', type: 'number', placeholder: '6' },
      { key: 'layout', label: 'Layout', type: 'select', options: ['grid', 'list', 'carousel'] },
    ],
  },
  blog: {
    label: 'Blog / Artículos',
    icon: <BookOpen size={16} />,
    description: 'Últimas entradas del blog',
    configFields: [
      { key: 'title', label: 'Título de sección', type: 'text', placeholder: 'Blog de viajes' },
      { key: 'max_items_featured', label: 'Nº máximo de artículos', type: 'number', placeholder: '3' },
    ],
  },
  testimonials: {
    label: 'Testimonios',
    icon: <Star size={16} />,
    description: 'Opiniones de clientes',
    configFields: [
      { key: 'title', label: 'Título', type: 'text', placeholder: 'Lo que dicen nuestros viajeros' },
    ],
  },
  gallery: {
    label: 'Galería de fotos',
    icon: <Image size={16} />,
    description: 'Galería visual de destinos',
    configFields: [
      { key: 'title', label: 'Título', type: 'text', placeholder: 'Nuestros destinos' },
    ],
  },
  faq: {
    label: 'Preguntas frecuentes',
    icon: <HelpCircle size={16} />,
    description: 'Sección de FAQ con acordeón',
    configFields: [
      { key: 'title', label: 'Título', type: 'text', placeholder: 'Preguntas frecuentes' },
    ],
  },
  newsletter: {
    label: 'Newsletter',
    icon: <Mail size={16} />,
    description: 'Captura de emails de suscripción',
    configFields: [
      { key: 'title', label: 'Título', type: 'text', placeholder: 'Únete a nuestra comunidad' },
      { key: 'subtitle', label: 'Subtítulo', type: 'text', placeholder: 'Recibe ofertas exclusivas' },
      { key: 'placeholder_text', label: 'Placeholder del input', type: 'text', placeholder: 'Tu correo electrónico' },
    ],
  },
  whatsapp_cta: {
    label: 'Botón flotante WhatsApp',
    icon: <MessageCircle size={16} />,
    description: 'Widget flotante de WhatsApp',
    configFields: [
      { key: 'phone_number', label: 'Número de WhatsApp', type: 'text', placeholder: '+52 55 0000 0000' },
      { key: 'message', label: 'Mensaje predeterminado', type: 'text', placeholder: 'Hola, quiero info sobre un viaje' },
      { key: 'position', label: 'Posición', type: 'select', options: ['bottom-right', 'bottom-left'] },
    ],
  },
  about: {
    label: 'Quiénes somos',
    icon: <Users size={16} />,
    description: 'Historia y valores de la agencia',
    configFields: [
      { key: 'title', label: 'Título', type: 'text', placeholder: 'Quiénes somos' },
      { key: 'body', label: 'Descripción', type: 'textarea', placeholder: 'Somos una agencia…' },
      { key: 'image_url', label: 'URL de imagen', type: 'url', placeholder: 'https://…' },
    ],
  },
};

const DEFAULT_SECTION_ORDER = [
  'hero', 'about', 'trips', 'gallery', 'testimonials', 'blog', 'faq', 'newsletter', 'whatsapp_cta',
];

/* ═══════════════════════════════════════════════════════════════
   UI Primitives
   ═══════════════════════════════════════════════════════════════ */

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest text-white/30 font-semibold">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Input({ value, onChange, placeholder, type = 'text' }: React.InputHTMLAttributes<HTMLInputElement> & { value: string; onChange: (e: React.ChangeEvent<HTMLInputElement>) => void }) {
  return (
    <input type={type} value={value} onChange={onChange} placeholder={placeholder}
      className="w-full px-3 py-2 text-sm rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-white/20 focus:ring-1 focus:ring-[var(--acc1)] outline-none transition-colors" />
  );
}

function Textarea({ value, onChange, placeholder }: { value: string; onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void; placeholder?: string }) {
  return (
    <textarea value={value} onChange={onChange} rows={4} placeholder={placeholder}
      className="w-full px-3 py-2 text-sm rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-white/20 focus:ring-1 focus:ring-[var(--acc1)] outline-none transition-colors resize-y" />
  );
}

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════ */

export function SiteBuilder({ addNotification }: { addNotification?: (n: any) => void }) {
  const [sections, setSections] = useState<SiteSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);

  // Drag state
  const dragSrcIdx = useRef<number | null>(null);

  const notify = useCallback((type: 'success' | 'error' | 'info', message: string) => {
    if (addNotification) addNotification({ type, message });
  }, [addNotification]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAdminSiteConfig();
      const existing = data.sections || [];
      // Ensure all known sections are present, merge with backend data
      const merged = DEFAULT_SECTION_ORDER.map((key, idx) => {
        const found = existing.find(s => s.key === key);
        return found ?? { key, is_enabled: ['hero', 'trips', 'whatsapp_cta'].includes(key), order: idx + 1, config: {} };
      });
      // Append any unknown sections from backend not in DEFAULT list
      existing.forEach(s => {
        if (!merged.find(m => m.key === s.key)) merged.push(s);
      });
      setSections(merged.sort((a, b) => a.order - b.order));
    } catch (e: any) { notify('error', e.message); }
    finally { setLoading(false); }
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  /* Toggle */
  const toggleSection = (key: string) => {
    setSections(prev => prev.map(s => s.key === key ? { ...s, is_enabled: !s.is_enabled } : s));
    setDirty(true);
  };

  /* Update config */
  const updateConfig = (key: string, field: string, value: string) => {
    setSections(prev => prev.map(s =>
      s.key === key ? { ...s, config: { ...s.config, [field]: value } } : s
    ));
    setDirty(true);
  };

  /* Reorder and fix order field */
  const reorder = (from: number, to: number) => {
    if (from === to) return;
    setSections(prev => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next.map((s, i) => ({ ...s, order: i + 1 }));
    });
    setDirty(true);
  };

  /* Drag handlers */
  const onDragStart = (e: React.DragEvent, idx: number) => {
    dragSrcIdx.current = idx;
    e.dataTransfer.effectAllowed = 'move';
  };
  const onDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragSrcIdx.current !== null && dragSrcIdx.current !== idx) {
      reorder(dragSrcIdx.current, idx);
      dragSrcIdx.current = idx;
    }
  };
  const onDrop = (e: React.DragEvent) => { e.preventDefault(); dragSrcIdx.current = null; };

  /* Save */
  const handleSave = async () => {
    setSaving(true);
    try {
      await updateAdminSiteConfig(sections);
      invalidateSiteConfigCache();
      notify('success', 'Cambios guardados — el sitio se actualizará en la próxima carga');
      setDirty(false);
    } catch (e: any) { notify('error', e.message); }
    finally { setSaving(false); }
  };

  const editingSection = sections.find(s => s.key === editingKey);
  const editingMeta = editingKey ? SECTION_META[editingKey] : null;

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-16 justify-center text-white/30 text-sm">
        <Loader size={16} className="animate-spin" />Cargando configuración…
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <LayoutTemplate size={20} style={{ color: 'var(--acc2)' }} />
            Constructor de tu Sitio Web
          </h1>
          <p className="text-xs text-white/30 mt-1">Activa secciones, reordena arrastrando y edita el contenido de cada una</p>
        </div>
        <button onClick={handleSave} disabled={saving || !dirty}
          className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold transition-all disabled:opacity-40"
          style={{ background: dirty ? 'var(--acc1)' : 'rgba(255,255,255,.08)', color: dirty ? 'var(--bg-dark)' : 'rgba(255,255,255,.3)' }}>
          {saving ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
          {saving ? 'Guardando…' : 'Guardar cambios'}
        </button>
      </div>

      {/* Sections list + optional side panel */}
      <div className="flex gap-4 items-start">
        {/* List */}
        <div className="flex-1 min-w-0 space-y-2">
          {sections.map((section, idx) => {
            const meta = SECTION_META[section.key];
            const isEditing = editingKey === section.key;
            return (
              <div
                key={section.key}
                draggable
                onDragStart={e => onDragStart(e, idx)}
                onDragOver={e => onDragOver(e, idx)}
                onDrop={onDrop}
                className={`o-card flex items-center gap-3 cursor-default transition-all select-none ${
                  isEditing ? 'ring-1 ring-[var(--acc1)] border-[rgba(var(--acc1-rgb),.3)]' : ''
                }`}
              >
                {/* Drag handle */}
                <div className="cursor-grab active:cursor-grabbing text-white/15 hover:text-white/40 transition-colors shrink-0 touch-none">
                  <GripVertical size={16} />
                </div>

                {/* Order bubble */}
                <div className="w-6 h-6 rounded-full bg-white/5 flex items-center justify-center text-[10px] text-white/30 shrink-0 font-mono">
                  {idx + 1}
                </div>

                {/* Icon */}
                <div className={`shrink-0 ${section.is_enabled ? 'text-white/40' : 'text-white/15'}`}>
                  {meta?.icon ?? <Globe size={16} />}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0" onClick={() => setEditingKey(isEditing ? null : section.key)}>
                  <p className={`text-sm font-semibold leading-tight cursor-pointer hover:text-white transition-colors ${
                    section.is_enabled ? 'text-white' : 'text-white/30'
                  }`}>
                    {meta?.label ?? section.key}
                  </p>
                  {meta?.description && (
                    <p className="text-[11px] text-white/20 mt-0.5">{meta.description}</p>
                  )}
                </div>

                {/* Config indicator */}
                {meta?.configFields && meta.configFields.length > 0 && (
                  <button onClick={() => setEditingKey(isEditing ? null : section.key)}
                    className="p-1.5 rounded-lg text-white/20 hover:text-white/50 hover:bg-white/5 transition-colors shrink-0" title="Editar config">
                    <ChevronRight size={14} className={`transition-transform ${isEditing ? 'rotate-90' : ''}`} />
                  </button>
                )}

                {/* Toggle */}
                <button onClick={() => toggleSection(section.key)}
                  title={section.is_enabled ? 'Desactivar' : 'Activar'}
                  className={`w-11 h-6 rounded-full relative transition-colors shrink-0 ${
                    section.is_enabled ? 'bg-emerald-500/40' : 'bg-white/10'
                  }`}>
                  <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                    section.is_enabled ? 'translate-x-5' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
            );
          })}

          {/* Unsaved hint */}
          {dirty && (
            <p className="text-[10px] text-amber-400/60 text-center pt-1">Cambios sin guardar — recuerda hacer clic en "Guardar cambios"</p>
          )}
        </div>

        {/* Side panel: config editor */}
        {editingSection && editingMeta && editingMeta.configFields.length > 0 && (
          <div className="w-72 shrink-0">
            <div className="o-card space-y-4 sticky top-4">
              {/* Panel header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-white/40">{editingMeta.icon}</span>
                  <span className="text-sm font-semibold text-white">{editingMeta.label}</span>
                </div>
                <button onClick={() => setEditingKey(null)}
                  className="p-1 text-white/20 hover:text-white/50 transition-colors rounded-lg hover:bg-white/5">
                  <X size={14} />
                </button>
              </div>

              {/* Config fields */}
              <div className="space-y-3 border-t border-white/[.06] pt-3">
                {editingMeta.configFields.map(field => (
                  <Field key={field.key} label={field.label}>
                    {field.type === 'textarea' ? (
                      <Textarea
                        value={editingSection.config[field.key] ?? ''}
                        onChange={e => updateConfig(editingSection.key, field.key, e.target.value)}
                        placeholder={field.placeholder}
                      />
                    ) : field.type === 'select' ? (
                      <select
                        value={editingSection.config[field.key] ?? (field.options?.[0] ?? '')}
                        onChange={e => updateConfig(editingSection.key, field.key, e.target.value)}
                        className="w-full px-3 py-2 text-sm rounded-lg bg-white/5 border border-white/10 text-white outline-none">
                        {field.options?.map(o => (
                          <option key={o} value={o} className="bg-[var(--bg-dark)]">{o}</option>
                        ))}
                      </select>
                    ) : (
                      <Input
                        type={field.type}
                        value={editingSection.config[field.key] ?? ''}
                        onChange={e => updateConfig(editingSection.key, field.key, e.target.value)}
                        placeholder={field.placeholder}
                      />
                    )}
                  </Field>
                ))}
              </div>

              <p className="text-[10px] text-white/15 pt-1 border-t border-white/[.06]">
                Los cambios se guardan al presionar "Guardar cambios" en la parte superior.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SiteBuilder;
